import { IBlockchain } from '@tacans/shared-marketplace-layer';
import React, { forwardRef, Ref } from 'react';
import { CURRENCY_SYMBOL, FIAT_FR_DIGITS_LENGTH } from 'src/constants/common.constants';
import { useCoins } from 'src/features/common/wallet/hooks/wallet.hooks';
import { formatCurrency, makePriceByNano } from 'src/utils/entity.utils';
import { useAllCurrencyEntities } from '../../hooks/currency.hooks';
import { StyledCurrencyWrapper, StyledCurrencyLabel, StyledCurrencyValue } from './styles';

export interface CurrencyPropsI {
    nanoPrice: number | string;
    testId?: string;
    toEURO?: boolean;
    fromEURO?: boolean;
    toUSD?: boolean;
    blockchainId: IBlockchain['id'];
    withoutCoinSymbol?: boolean;
}

const Currency = forwardRef(
    (
        { withoutCoinSymbol, nanoPrice, toUSD, fromEURO, toEURO, blockchainId, testId }: CurrencyPropsI,
        ref: Ref<HTMLSpanElement>
    ) => {
        const allCurrencyEntities = useAllCurrencyEntities();
        const coins = useCoins();

        const coinSymbol = coins[blockchainId]?.[0]?.symbol;
        const coinId = coins[blockchainId]?.[0]?.id;
        const currency = allCurrencyEntities[coinId];

        let price = Number(nanoPrice);
        if (toUSD && currency?.usd) {
            price = currency.usd * Number(nanoPrice);
        }
        if (toEURO && currency?.euro) {
            price = currency.euro * Number(nanoPrice);
        }
        if (fromEURO && currency?.euro) {
            price = Number(nanoPrice) / currency.euro;
        }

        const value = makePriceByNano(price);

        if (toEURO || toUSD) {
            return currency ? (
                <span data-test-id={testId && `${testId}-eur-usd`}>
                    ~ {toUSD && CURRENCY_SYMBOL.usd}
                    {toEURO && CURRENCY_SYMBOL.eur}
                    {formatCurrency(value, FIAT_FR_DIGITS_LENGTH)}
                </span>
            ) : null;
        }

        if (fromEURO) {
            return currency ? (
                <StyledCurrencyWrapper data-test-id={testId && `${testId}-usd`}>
                    <StyledCurrencyValue>{formatCurrency(value, 6)}</StyledCurrencyValue>
                    <StyledCurrencyLabel> {coinSymbol}</StyledCurrencyLabel>
                </StyledCurrencyWrapper>
            ) : null;
        }

        return (
            <StyledCurrencyWrapper data-test-id={testId && `${testId}-price`}>
                <StyledCurrencyValue ref={ref}>{formatCurrency(value, 6)}</StyledCurrencyValue>
                <StyledCurrencyLabel>{withoutCoinSymbol ? '' : coinSymbol || ''}</StyledCurrencyLabel>
            </StyledCurrencyWrapper>
        );
    }
);
export default Currency;

Currency.displayName = 'Currency';
